import { useEffect, useRef, useState } from 'react'

export default function Scratch({
  width = 320,
  height = 200,
  brushSize = 20,
  finishPercent = 80,
  fadeOutOnComplete = true,
  backgroundColor = '#808080',
  onComplete,
  children,
}) {
  const canvasRef = useRef(null)
  const ctxRef = useRef(null)

  const isDrawing = useRef(false)
  const lastPoint = useRef(null)
  const isFinished = useRef(false)
  const lastCheck = useRef(0)

  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctxRef.current = ctx

    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)

    setLoaded(true)
  }, [width, height, backgroundColor])

  const getMouse = (e) => {
    const canvas = canvasRef.current
    const { top, left } = canvas.getBoundingClientRect()

    let x, y

    if (e.touches) {
      x = e.touches[0].clientX - left
      y = e.touches[0].clientY - top
    } else {
      x = e.clientX - left
      y = e.clientY - top
    }

    return { x, y }
  }

  const distanceBetween = (p1, p2) => {
    if (!p1 || !p2) {
      return 0
    }

    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2)
  }

  const angleBetween = (p1, p2) => {
    if (!p1 || !p2) {
      return 0
    }

    return Math.atan2(p2.x - p1.x, p2.y - p1.y)
  }

  const getFilledInPixels = (stride = 128) => {
    const canvas = canvasRef.current
    const ctx = ctxRef.current

    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const total = pixels.data.length / stride
    let count = 0

    for (let i = 0; i < pixels.data.length; i += stride) {
      if (pixels.data[i] === 0) {
        count++
      }
    }

    return Math.round((count / total) * 100)
  }

  const handlePercentage = (percent = 0) => {
    if (isFinished.current) {
      return
    }

    if (percent >= finishPercent) {
      const canvas = canvasRef.current

      if (fadeOutOnComplete) {
        canvas.style.transition = 'opacity 1s'
        canvas.style.opacity = '0'
      }

      isFinished.current = true
      onComplete?.()
    }
  }

  const handlePointerDown = (e) => {
    isDrawing.current = true
    lastPoint.current = getMouse(e)
  }

  const handlePointerMove = (e) => {
    if (!isDrawing.current) {
      return
    }
    e.preventDefault()

    const ctx = ctxRef.current
    const currentPoint = getMouse(e)
    const distance = distanceBetween(lastPoint.current, currentPoint)
    const angle = angleBetween(lastPoint.current, currentPoint)

    ctx.globalCompositeOperation = 'destination-out'

    for (let i = 0; i < distance; i++) {
      const x = lastPoint.current.x + Math.sin(angle) * i
      const y = lastPoint.current.y + Math.cos(angle) * i

      ctx.beginPath()
      ctx.arc(x, y, brushSize, 0, Math.PI * 2)
      ctx.fill()
    }

    lastPoint.current = currentPoint

    const now = Date.now()
    if (now - lastCheck.current > 300) {
      lastCheck.current = now
      handlePercentage(getFilledInPixels())
    }
  }

  const handlePointerUp = () => {
    isDrawing.current = false
    handlePercentage(getFilledInPixels())
  }

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          zIndex: 1,
          touchAction: 'none',
        }}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onMouseMove={handlePointerMove}
        onTouchMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onTouchEnd={handlePointerUp}
      />

      <div
        style={{
          visibility: loaded ? 'visible' : 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  )
}
